'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

/**
 * SessionMonitor - Detects expired sessions on focus/visibility change
 *
 * Handles cases where:
 * - User leaves computer and session expires, then returns to tab
 * - User loses internet connection and session expires while offline, then reconnects
 * - User switches tabs and comes back to expired session
 *
 * Works in conjunction with inactivity timeout (which already handles active sessions)
 * SOC 2 Compliance: Ensures users are immediately redirected when session expires
 */
export function SessionMonitor() {
  const pathname = usePathname()
  const checkingRef = useRef(false)
  const lastCheckRef = useRef(Date.now())

  // Don't run on public routes
  const publicRoutes = ['/login', '/forgot-password', '/reset-password', '/mfa/setup', '/mfa/verify']
  const isPublicRoute = publicRoutes.some(route => pathname.startsWith(route))

  useEffect(() => {
    if (isPublicRoute) return

    const checkSession = async () => {
      // Prevent concurrent checks
      if (checkingRef.current) return

      // Rate limit: don't check more than once every 5 seconds
      const now = Date.now()
      if (now - lastCheckRef.current < 5000) return

      checkingRef.current = true
      lastCheckRef.current = now

      try {
        const response = await fetch('/api/auth/session', {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store'
        })

        if (response.status === 401) {
          // Only 401 means GoTrue actually rejected the session. 5xx
          // (Supabase blip, transient network on the server) used to
          // trip this branch too and boot users mid-session even though
          // their cookies were still valid — middleware re-checks auth
          // on the next real navigation, so let those pass.
          console.log('[SessionMonitor] Session expired, redirecting to login')
          window.location.href = '/login?session=expired'
        }
      } catch (error) {
        // Network error - don't redirect, might just be temporary
        console.warn('[SessionMonitor] Network error checking session:', error)
      } finally {
        checkingRef.current = false
      }
    }

    // Check session when window regains focus (user returns to tab)
    const handleFocus = () => {
      console.log('[SessionMonitor] Window focused, checking session')
      checkSession()
    }

    // Check session when online event fires (internet reconnects)
    const handleOnline = () => {
      console.log('[SessionMonitor] Network reconnected, checking session')
      checkSession()
    }

    // Check session when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[SessionMonitor] Page visible, checking session')
        checkSession()
      }
    }

    window.addEventListener('focus', handleFocus)
    window.addEventListener('online', handleOnline)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Initial check on mount
    checkSession()

    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('online', handleOnline)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isPublicRoute, pathname])

  return null // This component doesn't render anything
}
